import { WspDistrictFilterKey } from "./wsp";

export type FilterKey = AgencyId | AgencyAbbr | WspDistrictFilterKey | JogId; // This type is for use throughout the code where the "ID" could be a value from more than one type.
export type AgencyId = string; // Should be a guid or simple digit (eventually only digits, and changed to number type)

export interface Agency {
  abbreviation: AgencyAbbr;
  id: AgencyId;
  category: AgencyCategory;
  createdBy: string; // email address
  createdDate: string;
  isActive?: boolean;
  isFake?: boolean;
  isFilter: boolean;
  isUserAgency: boolean;
  jogIds?: string[]; // The JOGs an Agency is a part of
  lastModifiedBy?: string; // email address
  lastModifiedDate?: string;
  name: string;
  // primaryContactEmail?: string;
  // primaryContactName?: string;
  // primaryContactPhoneNumber?: string;
  // primaryContactUserId?: string;
  shortName: string;
}

export interface Jog {
  agencies: Agency[];
  id: JogId;
  isFilter: boolean;
  isUserJog: boolean;
  isGroupOnly: boolean;
  name: string;
  shortName: string;
}

export enum AgencyCategory {
  City = "City",
  DOT = "Transportation",
  EmergencyMgmt = "Emergency Mgmt",
  Fire = "Fire",
  LawEnforcement = "Law Enforcement",
  PublicTransit = "Transit",
  PublicUtilities = "Public Utilities",
  PublicWorks = "Public Works",
  Other = "Other",
}

export enum JogId {
  // Not actual JOG
  NOT_SELECTED = "",
  Wsp = "WSPJog", // No events associated with this ID. Just used to group all Districts for dispatch event filter group.
  Other = "00",  // "No JOG"

  // Actual JOG or Group
  SeattleArea = "01", // "SEA"
  KingCountyNortheast = "02", // "NEJOG"
  KingCountySouth = "03", // "South JOG"
  Tacoma = "04", // "TAC"
  JBLM = "05",
  SnohomishCounty = "06",
  KitsapPeninsula = "07",
  ClarkCounty = "08",
  LewisCounty = "10",
  LowerColumbiaRiver = "11",
  NortheastWa = "13",
  NorthwestWa = "17", // Includes SkagitCounty & WhatcomCounty.
  NorthCentralWa = "20",
  SoutheastWa = "15",
  SouthCentralWa = "22",

  // Group, not actual JOG.
  // Obsolete after release of new JOGs
  EasternWa = "18", // All area covered by WSP districts 3, 4, 6. Mainly added for IRT users in those areas.

  // Obsolete - Leaving commented to note ID has been used.
  // CowlitzCounty = "09",  // Combined into Lower Columbia River JOG.
  // ThurstonCounty = "14", // Will be part of North Central WA JOG instead.
  // SkagitCounty = "12",   // Replaced by NW WA JOG.
  // WhatcomCounty = "16",  // Replaced by NW WA JOG.
}

export const AllJogIds = Object.values(JogId).filter(id =>
  id !== JogId.NOT_SELECTED &&
  id !== JogId.Wsp &&
  id !== JogId.Other
);

// This should NOT be keptin sync with ALL IDs of Agencies in DynamoDB.
// But the code does reference these few IDs directly, at least for now.
export enum AgencyIds {
  KingCountyMetro = "b6086a8b-ac32-4970-b63c-87314318ff45",
  SeattleFD = "e43dc46a-ff45-4f01-a7c0-00adec1979f1",
  SeattlePD = "08b6c595-b878-48fe-860b-cb477ffc8212",
  WSP = "e83bda18-f3b9-42aa-959b-4dff695bf83f",
}

// todo: split "future agency" Abbreviations into a different set?
export enum AgencyAbbr {
  NOT_SELECTED = "",
  OTHER = "Other",

  // Statewide / Multiple JOG
  WSP = "WSP",
  WSDOT = "WSDOT",
  NWSA = "NWSA", // Northwest Sea Port alliance
  KCM = "KCM",

  // SAJOG #################################  
  SDOT = "SDOT",
  SFD = "SFD",
  SPD = "SPD",
  PortOfSeattle = "Port",
  SeattleEmergencyManagement = "SEAOEM",
  SeattleDeptOfConstructionAndInspections = "SDCI",

  // NEJOG #################################
  CityOfBellevue = "COB",
  BellevueFire = "BELFD",
  BellevueDot = "BELDOT",
  BellevuePolice = "BELPD",
  BothellEmergencyMgmt = "BOTHEM",
  BothellPolice = "BOTHPD",
  EastsideFire = "EFR",
  FallCityFire = "FcFD",
  IssaquahFire = "IssaFD",
  IssaquahPublicWorks = "IssaPW",
  KingCountyEmergencyMgmt = "KingCoEM",
  KirklandFire = "KirkFD",
  KirklandPublicWorks = "KirkPW",
  MedinaPD = "MDPD",
  MedinaPublicWorks = "MDPW",
  CityOfMercerIsland = "MI",
  MercerIslandPD = "MIPD",
  CityOfNewcastle = "NC",
  NewcastlePublicWorks = "NCPW",
  Norcom = "NORCOM",
  RedmondFire = "REDFD",
  RedmondPolice = "REDPD",
  RedmondPublicWorks = "REDPW",
  ShorelineEmergencyMgmt = "SHOREM",
  ShorelineFire = "SHOREFD",
  SnoqualmieFire = "SNOFD",
  ST = "ST",
  //SoundTransitKC Metro/Link LightRail
  WaEmergencyMgmtDiv = "WAEM",

  // South King County JOG (South JOG) ##########
  AuburnPolice = "AUBPD",
  CityOfSeaTac = "CoST",
  CityOfTukwila = "CoTW",
  CovingtonPolice = "COVPD",
  DesMoinesPublicWorks = "DMPW",
  DesMoinesPolice = "DMPD",
  FederalWayEmergencyMgmt = "FWEM",
  FederalWayPolice = "FWPD",
  FederalWayPublicWorks = "FWPW",
  KingCountyMedicOne = "KCMO",
  KentPolice = "KENTPD",
  MapleValleyPolice = "MVPD",
  MapleValleyPublicWorks = "MVPW",
  MapleValleyEmergencyMgmt = "MVEM",
  MountainViewFireAndRescue = "MTVFR",
  PierceTransit = "PT",
  PugetSoundRegionalFireAuthority = "PSF",
  SeaTacEmergencyManagement = "STEM",
  SeaTacPublicWorks = "STPW",
  //Sound Transit
  //Sound Transit- KC Metro/Link Light Rail
  TukwilaEmergencyManagement = "TWEM",
  TukwilaPolice = "TWPD",
  TukwilaPublicWorks = "TWPW",
  ValleyRegionalFireAuthority = "VRFA",
  //WA Emergency Management Division

  // Tacoma JOG #################################
  BonneyLakeEmergencyManagementt = "BLEM",
  BonneyLakePublicWorks = "BLPW",
  BuckelyFire = "BuckleyFire",
  CentralPierceFireAndRescue = "CPFR",
  CityOfTacomaVenuesAndEvents = "TacVenEvents",
  EastPierceFireAndRescue = "EPFR",
  GigHarborFire = "GigFire",
  GigHarborPolice = "GigPD",
  LakewoodPolice = "LWPD",
  MiltonPublicWorks = "MiltonPW",
  // NWSA / Port of Tacoma/Port of Seattle
  OrtingEmergencyManagement = "OrtEM",
  OrtingPolice = "OrtPD",
  PierceCountyEmergencyMgmt = "PierceEM",
  PierceCountyPlanningAndPublicWorks = "PiercePW",
  PierceCountySheriffsOffice = "PierceSheriff",
  //PierceTransit = "PierceTransit", // Tacoma and South KC JOG
  PortOfTacoma = "POT",
  PuyallupEmergencyManagement = "PuyEM",
  PuyallupPolice = "PuyPD",
  PuyallupPublicWorks = "PuyPW",
  //Sound Transit
  SouthSound911 = "SouthSound911",
  SteilacoomPD = "SMPD",
  SumnerEmergencyManagement = "SumEM",
  SumnerPublicWorks = "SumPW",
  TacomaFireOperations = "TacFireOps",
  TacomaFireEmergencyMngmt = "TacFireEM",
  TacomaPublicWorks = "TacPW",
  TacomaRail = "TacRail",
  UniversityPlacePolice = "UPPD",
  //WA Emergency Management Division
  WestPierceFireAndRescue = "WPFR",
  WestPierceFireAndRescueEmergancyMgmt = "WPFREM",

  // Snohomish County JOG ######################
  CityOfGoldBar = "GLD",
  CityOfSultan = "SUL",
  CityOfLakeStevens = "LKSTV",
  CityOfMarysville = "MARY",

  ArlingtonPolice = "ArlPD",
  // BothellPolice = "BothPD", // Also in NEJOG
  BrierPublicWorks = "BrierPW",
  CamanoIslandFireAndResue = "CIFR",
  CommunityTransit = "CommTransit",
  EdmondsPoplice = "EdPD",
  EdmondsPublicWorks = "EdPW",
  EverettFire = "EvFD",
  EverettPolice = "EvPD",
  EverettPublicWorks = "EvPW",
  GoldBarPolice = "GBPD",
  GoldBarPublicWorks = "GBPW",
  LakeStevensPolice = "LSPD",
  LakeStevensPublicWorks = "LSPW",
  LynnwoodPublicWorks = "LynnPW",
  MarysvilleFire = "MaryFD",
  MarysvillePD = "MARYPD",
  MarysvillePublicWorks = "MaryPW",
  MonroePolice = "MonPD",
  MountlakeTerracePublicWorks = "MLTPW",
  MukilteoPolice = "MukPD",
  NorthCountyRegionalFireAuthority = "NoCoFR",
  SnoCoEmergencyManagement = "SnoEM",
  SnohomishRegionalFireAndRescue = "SRFR",
  SoundTransit = "ST",
  SouthCountyFire = "SoCoFire",
  StanwoodCamanoSchoolDistrict = "SCSD", // Also part of NW WA JOG
  StanwoodPublicWorks = "StanPW",
  StillaguamishPoplice = "StillPD",
  SultanPublicWorks = "SulPW",
  TulalipTribalPolice = "TulPD",
  //WA Emergency Management Division

  // Kitsap Peninsula JOG #######################
  BainbridgeIslandFire = "BIFD",
  BainbridgeIslandEmergencyManagement = "BIEM",
  BainbridgeIslandPolice = "BIPD",
  BremertonFire = "BIFD",
  BremertonPolice = "BremPD",
  BremertonPublicWorks = "BremPW",
  BrinnonFire = "BrinFD",
  CentralKitsapFireAndRescue = "CenKitFR",
  ClallamTransit = "ClAmTransit",
  EastJeffersonFireAndRescue = "EJFR",
  JeffersonCountyDeptOfEmergencyManagement = "JCEM",
  JeffersonCountySheriffsOffice = "JCSO",
  JeffersonTransitAuthority = "JTA",
  KitsapCountyDeptOfEmergencyManagement = "KitCoEM",
  KitsapCountySheriffsOffice = "KitCoSO",
  KitsapTransit = "KitTran",
  MACECOM911 = "MACECOM911",
  MasonCountyDeptOfEmergencyManagement = "MasCoEM",
  MasonCountyPublicWorks = "MasCoPW",
  MasonTransitAuthority = "MasTA",
  NavyRegionNorthwestFire = "NavyRegNWF",
  NavyShipyardEmergencyManagement = "NavySYEM",
  NorthKitsapFireAndRescue = "NKitFR",
  PortAngelesFire = "PAFD",
  PortGambleSKlallamTribeEmergencyManagement = "PGSTEM",
  PortOrchardPolice = "POPD",
  PortOrchardPublicWorks = "POPW",
  PortTownsendPolice = "PTPD",
  PoulsboEmergencyManagement = "PoulsEM",
  PoulsboFire = "PoulsFD",
  PoulsboPublicWorks = "PoulsPW",
  QuilceneFire = "QuilceneFD",
  SequimPolice = "SeqPD",
  SequimPublicWorks = "SeqPW",
  SheltonPublicWorks = "ShelPW",
  SkokomishTribalPolice = "SkoPD",
  SouthKitsapFireAndRescue = "SKitFR",

  // Lower Columbia River JOG
  CityOfLongview = "LV",
  WahkiakumCounty = "WAHK",

  // Northwest WA JOG
  SouthWhatcomFireAuthority = "SWFA",
  TownofLaConner = "TOLC",
  WhatcomCountyFireDistrict7 = "WCFD7",

  // Clark County JOG
  ClarkCountyFireDistrict3 = "CCFD3",

}

// These are the 32 Agencies that existed before we created DynamoDB items for all Agencies and released the Agency Admin feature.
// This is for backwards compatibility of where agency fields may still be populated
// with an abbreviation instead of an ID. We do not need to add to this as new Agencies are created.
// This MAap could be eliminated two ways:
// 1) if we do a "data migration", where we change any stored abbreviation to an ID in DynamoDB. Or,
// 2) if we update the Agency items in DynamoDB to have a "LegacyId" and update the
// Agency objects in state, we could have a lookup lookup based on ID OR LegacyID.
// Note: I want to discourage referencing any IDs directly, so the AgencyId enum
// is only populated with some of these that are referenced already.
const AgencyIdConversionMap = new Map<AgencyAbbr, AgencyId>([
  [AgencyAbbr.EastsideFire, "f2281dc8-f898-4a4b-844c-2dc639ea5207"],
  [AgencyAbbr.ClarkCountyFireDistrict3, "2870c96c-f5b1-4b8f-a286-92b039963c95"],
  [AgencyAbbr.CamanoIslandFireAndResue, "70f49cb5-0914-4c0c-b918-5e046ba91a13"],
  [AgencyAbbr.CityOfBellevue, "99be4799-adcc-4b99-b263-8a63a2cb949f"],
  [AgencyAbbr.CityOfGoldBar, "1264d91e-b675-410a-96ee-381dcb8253a9"],
  [AgencyAbbr.KCM, AgencyIds.KingCountyMetro],
  [AgencyAbbr.CityOfLakeStevens, "fdfef506-0f94-4b33-806c-ee3d162774ef"],
  [AgencyAbbr.CityOfLongview, "3e88c7f4-113e-4cd2-8937-7e30029b59b9"],
  [AgencyAbbr.CityOfMarysville, "9fc305cf-fe38-4385-9e46-d84e9fc2e2d8"],
  [AgencyAbbr.MarysvillePD, "49a6db3e-34a1-4091-ab21-1bdaee8ea668"],
  [AgencyAbbr.MedinaPD, "01512b80-e53d-4848-a305-5c9727eab6c3"],
  [AgencyAbbr.CityOfMercerIsland, "f394c940-771a-4202-aeb5-64034d6fdb4c"],
  [AgencyAbbr.MercerIslandPD, "aa40cb52-1eca-4bd1-836f-85d532d8fd0c"],
  [AgencyAbbr.NWSA, "553f1cac-aad2-4c99-8f42-9953463e28bc"],
  [AgencyAbbr.PortOfSeattle, "35d38c0d-82a3-488a-924b-36089520362b"],
  [AgencyAbbr.PortOfTacoma, "ac099570-2351-446b-b9d5-a64661782d48"],
  [AgencyAbbr.StanwoodCamanoSchoolDistrict, "d2ac0f8d-362f-45bf-8b9b-c96f28f4fdc7"],
  [AgencyAbbr.SeattleDeptOfConstructionAndInspections, "41b7bae4-2eac-49fa-9858-70fe4ad0a6c8"],
  [AgencyAbbr.SDOT, "24e5abdc-bd35-426d-bcc1-bb4f1442badc"],
  [AgencyAbbr.SeattleEmergencyManagement, "0967238c-33bc-40e5-992d-70f3aec39b80"],
  [AgencyAbbr.SFD, AgencyIds.SeattleFD],
  [AgencyAbbr.SteilacoomPD, "be90f6a9-8819-426e-9302-1f191ef3fe01"],
  [AgencyAbbr.SPD, AgencyIds.SeattlePD],
  [AgencyAbbr.SnohomishRegionalFireAndRescue, "758a3592-20bf-4c43-84c3-026c8cc63ab5"],
  [AgencyAbbr.SoundTransit, "8d264520-8114-4073-849a-7901880082ef"],
  [AgencyAbbr.CityOfSultan, "5764f8ca-d511-4c54-9e27-b3240e290830"],
  [AgencyAbbr.SouthWhatcomFireAuthority, "e19fe22c-e252-42fc-91a1-7ca955e1dc94"],
  [AgencyAbbr.TownofLaConner, "c0d9228c-bb8c-4386-8009-9199a31b4887"],
  [AgencyAbbr.WahkiakumCounty, "50c98461-df5d-434a-af43-5f9ad4860c71"],
  [AgencyAbbr.WhatcomCountyFireDistrict7, "b1c103b5-0c4c-4408-af63-9856d4acf907"],
  [AgencyAbbr.WSDOT, "bbcb98a0-90f1-43ca-9fc7-a01d70a6e017"],
  [AgencyAbbr.WSP, AgencyIds.WSP],
]);

/**
 * A utility for backwards compatibility. When an abbreviation is passed as an ID,
 * the applicable ID is returned instead when found, otherwise the original value is returned.
 * @param agencyId  A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
export const getAgencyId = (agencyId: AgencyId | AgencyAbbr): AgencyId => {
  // If we change dispatch processing to start populating "agency" with the agencyId,
  // this still works because that value will simply be returned when no match is 
  // found in the conversion map.
  return AgencyIdConversionMap.get(agencyId as AgencyAbbr) || agencyId;
}

/**
 * A utility for backwards compatibility. Think of this as "get agency safely." It works with Agency IDs and is backwards compatible with agency abbreviations.
 * 
 * @param agenciesById Usually the agenciesById object retreived from the AgenciesState, passed in as store.agenciesById or agencyStore.agenciesById
 * @param agencyId A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
// todo: refactor this into a function in the useAgencies hook
export const GetAgency = (agenciesById: Record<string, Agency>, agencyId: AgencyId | AgencyAbbr | undefined): Agency | undefined => {
  return agenciesById[getAgencyId(agencyId || "")];
}

/**
 * This works with Agency IDs, and is backwards compatible to work with agency abbreviations.
 * 
 * @param agenciesById Usually the agenciesById object retreived from the AgenciesState, passed in as store.agenciesById or agencyStore.agenciesById
 * @param agencyId A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
export const GetAgencyAbbr = (agenciesById: Record<string, Agency>, agencyId: AgencyId | AgencyAbbr): string => {
  return GetAgency(agenciesById, agencyId)?.abbreviation || agencyId;
}

// Values that are saved in the dispatch event areaCode/ac field by dispatch processing
export enum AreaCode {
  I5 = "I5",
  WSP_D1 = "D1",
  WSP_D2 = "D2",
  WSP_D3 = "D3",
  WSP_D4 = "D4",
  WSP_D5 = "D5",
  WSP_D6 = "D6",
  WSP_D7 = "D7",
  WSP_D8 = "D8",
}

// Values that are saved in the dispatch event regionCode/rc field by dispatch processing
export enum RegionCode {
  NW = "NW",    // Northwest
  SEA = "SEA",  // King County,
  TAC = "TAC",  // Thurston, Peirce counties,
  SW = "SW",    // Southwest
  NC = "NC",    // North Central
  S = "S",      // South Central and Southeast
  E = "E",      // East
  PEN = "PEN",  // Olympic Peninsula
}
